import { useState, useRef, useEffect } from 'preact/hooks';
import type { ProjectTag } from '@voice-assistant/shared';

interface ProjectChipsProps {
  sessionId: string;
  projects: string[];
  pinnedProject: string | null;
  allProjectTags: ProjectTag[];
  onPin: (sessionId: string, project: string) => void;
  onUnpin: (sessionId: string) => void;
}

/**
 * Renders auto-detected project chips in the chat header.
 * Primary chip (first) has accent fill; secondary chips are outlined.
 * Tap → dropdown picker to override/pin the primary project.
 * Pinned chip shows a 📌 icon; tap × to unpin.
 */
export function ProjectChips({ sessionId, projects, pinnedProject, allProjectTags, onPin, onUnpin }: ProjectChipsProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
        setFilter('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [pickerOpen]);

  if (!projects || projects.length === 0) return null;

  const primary = projects[0];
  const isPinned = !!pinnedProject;

  // Label helper
  const getLabel = (key: string) => {
    const tag = allProjectTags.find((t) => t.key === key);
    return tag?.label ?? key;
  };

  const filtered = allProjectTags.filter((t) => {
    const q = filter.toLowerCase();
    return !q || t.key.includes(q) || t.label.toLowerCase().includes(q) || t.aliases.some((a) => a.includes(q));
  });

  return (
    <div class="project-chips">
      {projects.map((key, i) => (
        <button
          key={key}
          type="button"
          class={`project-chip${i === 0 ? ' project-chip--primary' : ' project-chip--secondary'}${key === pinnedProject ? ' project-chip--pinned' : ''}`}
          onClick={() => {
            if (i === 0) {
              setPickerOpen((v) => !v);
              setFilter('');
            }
          }}
          title={i === 0 ? 'Click to change primary project' : key}
        >
          {key === pinnedProject && <span class="project-chip-pin">📌</span>}
          {getLabel(key)}
          {key === pinnedProject && (
            <span
              class="project-chip-unpin"
              onClick={(e) => { e.stopPropagation(); onUnpin(sessionId); }}
              title="Unpin project"
            >×</span>
          )}
        </button>
      ))}

      {pickerOpen && (
        <div class="project-picker" ref={pickerRef}>
          <input
            class="project-picker-search"
            type="text"
            placeholder="Search project…"
            value={filter}
            onInput={(e) => setFilter((e.target as HTMLInputElement).value)}
            autoFocus
          />
          <div class="project-picker-list">
            {isPinned && (
              <button
                type="button"
                class="project-picker-item project-picker-item--unpin"
                onClick={() => { onUnpin(sessionId); setPickerOpen(false); setFilter(''); }}
              >
                ✕ Unpin (use auto-detection)
              </button>
            )}
            {filtered.map((tag) => (
              <button
                key={tag.key}
                type="button"
                class={`project-picker-item${tag.key === pinnedProject ? ' project-picker-item--active' : ''}`}
                onClick={() => { onPin(sessionId, tag.key); setPickerOpen(false); setFilter(''); }}
              >
                {tag.label}
                <span class="project-picker-item-key">{tag.key}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
